import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/apiClient";
import {
  severityMeta,
  statusFlagMeta,
  riskFlagMeta,
  FlagSeverity,
  FlagStatus,
} from "../lib/riskFlagMeta";
import { citationFor } from "../lib/ncStatutes";

// RiskFlagList — renders the per-property risk-intelligence flags from
// GET /api/v1/properties/:id/flags. Each flag is a severity-colored chip
// showing its title + a status badge (Detected / Possible / Not checked),
// expandable to reveal the detail copy and resolved statute/case citations.
//
// Design notes (from docs/nc-tax-foreclosure implementation guide):
//   - Three-state status is the spine. not_checked flags are the "known
//     unknowns" and MUST be visually distinct (a dashed, muted treatment) —
//     never let absence of a detected flag read as "all clear."
//   - Every flag carries a citable basis. A statuteRef that resolves through
//     ncStatutes.ts renders a citation chip; an unknown ref renders a neutral
//     "basis not yet documented" chip rather than asserting an uncited claim.
//   - Dependency-light: a plain expand/collapse, no popover/accordion library.
//   - Never crash the detail page: on empty or error, render nothing.

// Mirrors the backend Flag contract returned by /properties/:id/flags.
interface RiskFlag {
  flagType: string;
  severity: FlagSeverity | string;
  status: FlagStatus | string;
  title: string;
  detail: string;
  statuteRefs?: string[];
  disclaimerRef?: string;
  evidenceSource?: string;
}

interface FlagsResponse {
  flags: RiskFlag[];
}

// Phase-1 evaluators (backend/internal/riskflags/evaluators.go) all stamp
// this identical, uninformative marker. It's meaningful plumbing for the
// future Phase-2 data-driven evidence sources, but showing it verbatim today
// just leaks a backend implementation detail into the UI.
const GENERIC_EVIDENCE_SOURCE = "rule:always-on";

interface RiskFlagListProps {
  propertyId: string;
}

// A single citation chip — resolves the backend ref through ncStatutes.ts.
// Falls back to a neutral "basis not yet documented" chip for unknown refs so
// we never present an uncited legal claim (guardrail #4).
const CitationChip: React.FC<{ refStr: string }> = ({ refStr }) => {
  const cite = citationFor(refStr);
  if (cite) {
    const label = cite.label;
    const chip = (
      <span className="risk-flag-cite" title={`${cite.citation} — ${cite.summary}`}>
        {label}
      </span>
    );
    return cite.url ? (
      <a
        className="risk-flag-cite-link"
        href={cite.url}
        target="_blank"
        rel="noopener noreferrer"
        title={`${cite.citation} — ${cite.summary}`}
      >
        {label}
      </a>
    ) : (
      chip
    );
  }
  // Unknown ref — show the raw ref text but flag it as undocumented basis.
  return (
    <span
      className="risk-flag-cite risk-flag-cite--undocumented"
      title="No statute or case is mapped for this reference yet — verify with NC counsel."
    >
      {refStr} (basis not yet documented)
    </span>
  );
};

const RiskFlagCard: React.FC<{ flag: RiskFlag }> = ({ flag }) => {
  const [open, setOpen] = useState(false);
  const sev = severityMeta(flag.severity);
  const st = statusFlagMeta(flag.status);
  const meta = riskFlagMeta(flag.flagType);
  const isNotChecked = st.key === "not_checked";

  const refs = flag.statuteRefs ?? [];

  return (
    <li
      className={
        "risk-flag-card" +
        ` risk-flag-card--${sev.key}` +
        ` risk-flag-card--status-${st.key}` +
        (isNotChecked ? " risk-flag-card--not-checked" : "")
      }
      style={{ ["--risk-flag-color" as any]: `var(${sev.cssVar})` }}
    >
      <button
        type="button"
        className="risk-flag-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="risk-flag-icon" aria-hidden="true">
          {meta.icon}
        </span>
        <span className="risk-flag-title">{flag.title || meta.label}</span>
        <span
          className={`risk-flag-status risk-flag-status--${st.key}`}
          title={st.short}
        >
          {st.label}
        </span>
        <span className="risk-flag-toggle" aria-hidden="true">
          {open ? "–" : "+"}
        </span>
      </button>

      {open && (
        <div className="risk-flag-detail">
          {flag.detail && <p className="risk-flag-detail-text">{flag.detail}</p>}
          <p className="risk-flag-status-note">
            <strong>{st.label}:</strong> {st.short}
          </p>
          {refs.length > 0 && (
            <div className="risk-flag-cites">
              <span className="risk-flag-cites-label">Basis:</span>
              {refs.map((r, i) => (
                <CitationChip key={`${r}-${i}`} refStr={r} />
              ))}
            </div>
          )}
          {flag.evidenceSource && flag.evidenceSource !== GENERIC_EVIDENCE_SOURCE && (
            <p className="risk-flag-source">Source: {flag.evidenceSource}</p>
          )}
        </div>
      )}
    </li>
  );
};

const RiskFlagList: React.FC<RiskFlagListProps> = ({ propertyId }) => {
  const [flags, setFlags] = useState<RiskFlag[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<FlagsResponse>(`/properties/${propertyId}/flags`)
      .then(({ data }) => {
        if (cancelled) return;
        setFlags(Array.isArray(data?.flags) ? data.flags : []);
      })
      .catch(() => {
        // Never crash the detail page — degrade to "no flags shown".
        if (!cancelled) setFlags([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  // Loading: render nothing (the detail page already has plenty of content;
  // a spinner here would just add noise). Empty / error: also render nothing.
  if (loading || flags.length === 0) return null;

  return (
    <ul className="risk-flag-list">
      {flags.map((flag, i) => (
        <RiskFlagCard key={`${flag.flagType}-${i}`} flag={flag} />
      ))}
    </ul>
  );
};

export default RiskFlagList;
